// =============================================================================
// Admin Analytics — a fetcher that keeps partial data
// =============================================================================
// `useCustomFetcher` rejects the whole response on any GraphQL error, which
// would throw away every healthy cell because one failed. Every analytics
// request goes through this fetcher instead. It keeps `response.data`, records
// which `adminAnalytics` cells errored, and never rejects: a request with no
// usable data resolves as `requestFailed`. An error inside a cell (e.g. on its
// `previousValue`) leaves the cell's data in place with that one field null.
//
// Nothing that reaches a log sink carries response data or filter values:
// cells hold learner names, and the filter variables can too.
// =============================================================================
import { datadogRum } from "@datadog/browser-rum";
import { redactErrorVariables } from "@utils/redactErrorVariables";
import { ClientError, Variables } from "graphql-request";

import { FronteraGraphQLClient } from "@api/graphql/useCustomFetcher";
import { useAuthorization } from "@api/useAuthorization";

export interface PartialResult<TData> {
  data: TData | undefined;
  /** `adminAnalytics` cells whose own field errored (not just a field inside them). */
  failedFields: string[];
  /** Nothing usable came back (network failure, auth, or a root-level error). */
  requestFailed: boolean;
  /** The response carried errors somewhere, even inside a cell whose data was kept. */
  hadErrors?: boolean;
}

interface FetchOptions {
  /** Toast a whole-request failure (the dashboard). Single-cell and drilldown failures render inline. */
  toastOnFailure?: boolean;
}

type GraphQLErrorEntry = NonNullable<ClientError["response"]["errors"]>[number];

/** An error on the cell field itself: `["adminAnalytics", <cell>]`. Deeper paths are inside a cell. */
const failedCellsOf = (errors: readonly GraphQLErrorEntry[]): string[] => [
  ...new Set(
    errors
      .filter((e) => e.path?.length === 2 && e.path[0] === "adminAnalytics")
      .map((e) => e.path?.[1])
      .filter((field): field is string => typeof field === "string"),
  ),
];

// Server messages FDM's analytics resolvers raise on purpose. Anything else is
// logged as unrecognized: a database error can echo a filter value back.
const SAFE_SERVER_MESSAGES = new Set([
  "Analytics request took too long; narrow the filters.",
  "Analytics query took too long; narrow the filters.",
  "Invalid provider id.",
  "Permission denied",
  "Authentication required",
  // `handleApiError` keys its logout path on these two.
  "Not Authorized",
  "User not found",
]);
const safeMessage = (message: string | undefined): string =>
  message && SAFE_SERVER_MESSAGES.has(message) ? message : "Unrecognized server error";

const operationNameOf = (document: string): string => /\bquery\s+(\w+)/.exec(document)?.[1] ?? "unknown";

/** What a log sink may see: operation, error paths and known-safe messages. No data, no variables. */
const logContext = (document: string, errors: readonly GraphQLErrorEntry[]) => ({
  operation: operationNameOf(document),
  errors: errors.map((e) => ({ path: (e.path ?? []).join("."), message: safeMessage(e.message) })),
});

/**
 * Strip response data and variables from a `ClientError` before anything logs it.
 * Its constructor bakes `JSON.stringify({ response, request })` into `message`
 * and `stack`; keep the server's own errors so auth handling and the toast work.
 */
const stripClientError = (err: ClientError, document: string): void => {
  const summary = `Admin Analytics request failed: ${JSON.stringify(logContext(document, err.response.errors ?? []))}`;
  err.response.data = undefined;
  // graphql-request 7 also keeps the raw response text, which holds the same rows.
  err.response.body = "";
  err.request.variables = undefined;
  err.message = summary;
  err.stack = `ClientError: ${summary}`;
};

const isAbort = (err: unknown, signal: AbortSignal | undefined) =>
  signal?.aborted || (err instanceof Error && err.name === "AbortError");

const AUTH_FAILURE = /Not Authorized|User not found/;

type ApiErrorHandler = (err: Error, document: string) => Promise<unknown>;

/** The healthy cells of a response that errored in places, or nothing if it carried no data. */
const partialResultOf = <TData extends { adminAnalytics?: unknown }>(
  err: unknown,
  document: string,
): PartialResult<TData> | undefined => {
  if (!(err instanceof ClientError)) return undefined;
  const data = err.response.data as TData | undefined;
  if (!data?.adminAnalytics) return undefined;
  const errors = err.response.errors ?? [];
  // The failing cells render their own error states; log, but don't toast.
  datadogRum.addError(new Error("Admin Analytics partial response"), {
    type: "fdmApiPartialError",
    ...logContext(document, errors),
  });
  return { data, failedFields: failedCellsOf(errors), requestFailed: false, hadErrors: true };
};

/**
 * Report a request that returned nothing usable. The dashboard (and any auth
 * failure, so the app can log the user out) goes through the app's handler,
 * which toasts; everything else only logs, because each cell renders its own
 * error state.
 */
const reportFailure = async (
  err: unknown,
  document: string,
  toastOnFailure: boolean,
  handleApiError: ApiErrorHandler,
): Promise<void> => {
  const errors = err instanceof ClientError ? err.response.errors ?? [] : [];
  const authFailure = AUTH_FAILURE.test(String(err));
  if (err instanceof ClientError) stripClientError(err, document);
  if (!toastOnFailure && !authFailure) {
    datadogRum.addError(new Error("Admin Analytics request failed"), {
      type: "fdmApiRequestError",
      ...logContext(document, errors),
    });
    return;
  }
  try {
    // Logs, toasts, and handles auth failures; it always rethrows.
    await handleApiError(err as Error, document);
  } catch {
    // Each cell renders its own error state.
  }
};

export const usePartialDataFetcher = <TData extends { adminAnalytics?: unknown }, TVariables extends Variables>(
  document: string,
  { toastOnFailure = false }: FetchOptions = {},
) => {
  const { getAuthorizationHeader, handleApiError } = useAuthorization();

  return async (variables: TVariables, signal?: AbortSignal): Promise<PartialResult<TData>> => {
    try {
      const data = await FronteraGraphQLClient.request<TData>({
        document,
        variables,
        requestHeaders: await getAuthorizationHeader(),
        signal,
      });
      return { data, failedFields: [], requestFailed: false };
    } catch (err) {
      // A superseded request: let React Query treat it as cancelled.
      if (isAbort(err, signal)) throw err;
      redactErrorVariables(err);
      const partial = partialResultOf<TData>(err, document);
      if (partial) return partial;
      await reportFailure(err, document, toastOnFailure, handleApiError);
      return { data: undefined, failedFields: [], requestFailed: true };
    }
  };
};
